import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../utils/firebaseClient';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { FaEnvelope, FaLock, FaBullseye } from 'react-icons/fa';

const AuthPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '' });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) navigate('/create');
    });
    return unsub;
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      if (isForgotPassword) {
        await sendPasswordResetEmail(auth, formData.email);
        setSuccessMessage('Recovery email sent! Please check your inbox.');
      } else if (isLogin) {
        await signInWithEmailAndPassword(auth, formData.email, formData.password);
        navigate('/create');
      } else {
        await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        navigate('/create');
      }
    } catch (err) {
      const messages = {
        'auth/user-not-found': 'No account found with this email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/email-already-in-use': 'This email is already registered.',
        'auth/weak-password': 'Password must be at least 6 characters.',
        'auth/invalid-email': 'Invalid email address.',
        'auth/invalid-credential': 'Invalid email or password.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
      };
      setError(messages[err.code] || err.message);
    } finally {
      setLoading(false);
    }
  };

  const title = isForgotPassword ? 'Recover Password' : isLogin ? 'Login' : 'Sign Up';

  return (
    <div className="auth-container">
      <div className="auth-box">
        <Link to="/" className="auth-logo">
          <FaBullseye className="auth-logo-icon" />
          <span>TargetWise</span>
        </Link>

        <h2>{title}</h2>

        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <FaEnvelope className="input-icon" />
            <input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          {!isForgotPassword && (
            <div className="input-group">
              <FaLock className="input-icon" />
              <input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
          )}

          {isLogin && !isForgotPassword && (
            <div className="forgot-password">
              <button
                type="button"
                className="toggle-button"
                onClick={() => { setIsForgotPassword(true); setError(null); setSuccessMessage(null); }}
              >
                Forgot your password?
              </button>
            </div>
          )}

          <button type="submit" disabled={loading}>
            {loading ? 'Processing...' : isForgotPassword ? 'Send recovery email' : isLogin ? 'Login' : 'Sign Up'}
          </button>
        </form>

        <p className="toggle-auth">
          {isForgotPassword ? (
            <button className="toggle-button" onClick={() => { setIsForgotPassword(false); setError(null); setSuccessMessage(null); }}>
              Back to Login
            </button>
          ) : (
            <>
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button className="toggle-button" onClick={() => { setIsLogin(!isLogin); setError(null); setSuccessMessage(null); }}>
                {isLogin ? 'Sign Up' : 'Login'}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
